import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BurgerMenu, PhoneScreen as KitPhoneScreen } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeedbackStore } from '@/stores/useFeedbackStore';
import { ClinicPicker } from '@/pages/doctor/today/ClinicPicker';
import { useMemo, useState } from 'react';
import { buildItems, countLanes } from '../model';
import { useMyDay, useWorklist } from '../api';

export type Door = 'worklist' | 'myday' | 'stock';

/**
 * THE TECHNICIAN'S PHONE SCREEN — the kit's `PhoneScreen` wearing this lane's ☰.
 *
 * The top strip, the scroll and the burger button are the kit's now: five other
 * lanes draw the same shape and were each about to write it again. What stays
 * here is the only part that could not be shared — WHICH DOORS THIS ROLE HOLDS
 * and the live counts under them. A technician's Worklist and a doctor's Today
 * are not interchangeable, and no generic list could carry either.
 */
export function PhoneScreen({
  door,
  title,
  sub,
  crumb,
  onBack,
  trailing,
  children,
}: {
  door: Door;
  title: ReactNode;
  /** A caption under the title ("Maadi"). */
  sub?: ReactNode;
  /** The muted "Worklist ▸" before the title. */
  crumb?: ReactNode;
  onBack?: () => void;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <KitPhoneScreen
      className="tech-phone"
      title={title}
      sub={sub}
      crumb={crumb}
      onBack={onBack}
      trailing={trailing}
      menu={(close) => <TechMenu door={door} onClose={close} />}
    >
      {children}
    </KitPhoneScreen>
  );
}

function TechMenu({ door, onClose }: { door: Door; onClose: () => void }) {
  const navigate = useNavigate();
  const { person, branch } = useShellIdentity();
  const logout = useAuthStore((s) => s.logout);
  const openFeedback = useFeedbackStore((s) => s.setOpen);
  const list = useWorklist();
  const day = useMyDay();
  const [picker, setPicker] = useState(false);
  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  /* Counted in ROWS, the way the worklist draws them (a bundle is one row) —
     the server's per-task counts disagree with the chips beside them. */
  const c = useMemo(
    () => (list.data ? countLanes(buildItems(list.data.tasks)) : undefined),
    [list.data],
  );
  const dilating = (list.data?.tasks ?? []).filter((t) => t.lane === 'waiting' && t.waiting?.label === 'dilating').length;
  const waitingLine = c?.waiting
    ? dilating === c.waiting ? `${c.waiting} waiting on dilation` : `${c.waiting} waiting`
    : '';
  const rate = day.data?.rates?.[0];
  const home = <span className="chip chip-brand chip-sm">Home</span>;
  return (
    <>
      <BurgerMenu
        open
        onClose={onClose}
        roleChip="Technician"
        doors={[
          {
            key: 'worklist',
            icon: 'list-check',
            label: 'Worklist',
            sub: c ? [`${c.ready} ready`, waitingLine].filter(Boolean).join(' · ') : undefined,
            badge: door === 'worklist' ? home : undefined,
            onSelect: () => go('/technician/worklist'),
          },
          {
            key: 'myday',
            icon: 'chart-bar',
            label: 'My day',
            sub: day.data
              ? [`${day.data.today.performed} today`, rate ? `commission ${rate.percent}% of ${rate.label}` : ''].filter(Boolean).join(' · ')
              : undefined,
            badge: door === 'myday' ? home : undefined,
            onSelect: () => go('/technician/day'),
          },
          {
            key: 'stock',
            icon: 'packages',
            label: 'Stock',
            sub: 'the shelf, read-only',
            badge: door === 'stock' ? home : undefined,
            onSelect: () => go('/technician/stock'),
          },
        ]}
        note="Capture opens from an order, the bundle from a patient — never from here. No Directory: technicians neither dispatch nor order."
        account={person ? {
          name: person.name,
          initials: person.initials,
          caption: 'account — devices · notifications · signature',
          onSelect: () => go('/settings'),
        } : undefined}
        branch={branch}
        onBranch={() => setPicker(true)}
        onAppearance={() => go('/settings#appearance')}
        onFeedback={() => {
          onClose();
          openFeedback(true);
        }}
        onSignOut={() => {
          onClose();
          void logout().finally(() => navigate('/login', { replace: true }));
        }}
      />
      <ClinicPicker open={picker} onClose={() => setPicker(false)} />
    </>
  );
}
